import { useEffect, useState, useMemo, memo } from "react"
import firebase from "firebase"
import { db } from "../../../utils/firebase"
import Checkbox from "../../../components/ui/checkbox/checkbox"

// ChartJS imports
import { Line } from "react-chartjs-2"
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend } from "chart.js"

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend)

const TARGET_MARGIN = 450000
const DAY_MS = 24 * 60 * 60 * 1000
const WEEKDAY_COUNT = 7
const TEST_ACCOUNT_IDS = ["apH2Vpa4qns0YTnelDMX", "UPyzRTUdKGlJgiCCxseP", "RxnT7D5WGjPddb9dSCp4"]

const createWeekdayArray = (initialValue = 0) => Array.from({ length: WEEKDAY_COUNT }, () => initialValue)

// Helper function to get color based on year offset
const getColorForYear = (yearOffset) => {
	if (yearOffset === 0) return { border: "#e41f13", bg: "#e41f13" }

	const colors = [
		{ r: 59, g: 130, b: 246 },
		{ r: 168, g: 85, b: 247 },
		{ r: 34, g: 197, b: 94 },
		{ r: 251, g: 146, b: 60 },
		{ r: 20, g: 184, b: 166 },
	]

	const colorIndex = Math.min(yearOffset - 1, colors.length - 1)
	const color = colors[colorIndex]
	const opacity = 0.6

	return {
		border: `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`,
		bg: `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`,
	}
}

const formatDayMonthKey = (date) => {
	return date.toLocaleDateString("en-GB", {
		day: "2-digit",
		month: "2-digit",
	})
}

const toLocalDateKey = (date) => {
	const year = date.getFullYear()
	const month = String(date.getMonth() + 1).padStart(2, "0")
	const day = String(date.getDate()).padStart(2, "0")
	return `${year}-${month}-${day}`
}

const toLocalDateKeyFromSeconds = (seconds) => {
	if (!seconds && seconds !== 0) return null
	return toLocalDateKey(new Date(seconds * 1000))
}

const getStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

const addDays = (date, days) => {
	const next = new Date(date)
	next.setDate(next.getDate() + days)
	return next
}

const isValidClient = (clientID) => Boolean(clientID) && !TEST_ACCOUNT_IDS.includes(clientID)

const getCumulativeValueForDate = (dateKey, dateKeys, cumulativeByDate) => {
	if (cumulativeByDate[dateKey] && cumulativeByDate[dateKey] !== 0) {
		return cumulativeByDate[dateKey]
	}

	const dateIndex = dateKeys.indexOf(dateKey)
	for (let i = dateIndex - 1; i >= 0; i--) {
		const previousDate = dateKeys[i]
		if (cumulativeByDate[previousDate] && cumulativeByDate[previousDate] !== 0) {
			return cumulativeByDate[previousDate]
		}
	}

	return 0
}

const getMetricForWeekday = (weekdayValues, weekdayIndex, fallbackValue = 0) => {
	const metric = weekdayValues?.[weekdayIndex]
	return Number.isFinite(metric) ? metric : fallbackValue
}

const rollForecastIntoIncludedDays = ({ dailyForecastByDate, periodDates, includeSaturdays, includeSundays }) => {
	const isIncludedDay = (date) => {
		const day = date.getDay()
		if (!includeSaturdays && day === 6) return false
		if (!includeSundays && day === 0) return false
		return true
	}

	const includedDates = periodDates.filter((date) => isIncludedDay(date))
	if (includedDates.length === 0) return {}

	const lastIncludedDate = includedDates[includedDates.length - 1]
	const periodDateByKey = periodDates.reduce((acc, date) => {
		acc[toLocalDateKey(date)] = date
		return acc
	}, {})

	const rolledForecastByDate = {}
	Object.entries(dailyForecastByDate).forEach(([dateKey, value]) => {
		if (!value) return

		const sourceDate = periodDateByKey[dateKey]
		if (!sourceDate) return

		let targetDate = sourceDate
		if (!isIncludedDay(sourceDate)) {
			targetDate = includedDates.find((candidateDate) => candidateDate.getTime() > sourceDate.getTime()) || lastIncludedDate
		}

		const targetDateKey = toLocalDateKey(targetDate)
		rolledForecastByDate[targetDateKey] = (rolledForecastByDate[targetDateKey] || 0) + value
	})

	return rolledForecastByDate
}

const buildConversionAndLagModel = ({ openEnquiriesSnapshot, removedEnquiriesSnapshot, enquiryBookingsSnapshot, bookingsCreatedSnapshot }) => {
	const openEnquiries = openEnquiriesSnapshot.docs
		.filter((doc) => doc.data().archived_from_stats !== true)
		.map((doc) => {
			const data = doc.data()
			const createdSeconds = data.created?.seconds
			if (!createdSeconds || !isValidClient(data.client)) return null

			return {
				createdMs: createdSeconds * 1000,
				createdDateKey: toLocalDateKeyFromSeconds(createdSeconds),
				weekdayIndex: new Date(createdSeconds * 1000).getDay(),
				client: data.client,
				location: data.location || "",
			}
		})
		.filter(Boolean)

	const removedEnquiries = removedEnquiriesSnapshot.docs
		.filter((doc) => doc.data().archived_from_stats !== true)
		.map((doc) => {
			const data = doc.data().enquiry || {}
			const createdSeconds = data.created?.seconds
			if (!createdSeconds || !isValidClient(data.client)) return null

			return {
				createdMs: createdSeconds * 1000,
				createdDateKey: toLocalDateKeyFromSeconds(createdSeconds),
				weekdayIndex: new Date(createdSeconds * 1000).getDay(),
				client: data.client,
				location: data.location || "",
			}
		})
		.filter(Boolean)

	const allEnquiries = [...openEnquiries, ...removedEnquiries]

	const enquiryBookings = enquiryBookingsSnapshot.docs
		.map((doc) => {
			const data = doc.data()
			const enquiryCreatedSeconds = data.enquiry?.created?.seconds
			const bookingCreatedSeconds = data.created?.seconds
			if (!enquiryCreatedSeconds || !bookingCreatedSeconds || !isValidClient(data.client)) return null

			return {
				key: `${data.client}__${data.enquiry?.location || ""}__${toLocalDateKeyFromSeconds(enquiryCreatedSeconds)}`,
				bookingCreatedMs: bookingCreatedSeconds * 1000,
				client: data.client,
			}
		})
		.filter(Boolean)

	const directBookings = bookingsCreatedSnapshot.docs
		.map((doc) => {
			const data = doc.data()
			if (data.booking_type !== "DIRECT") return null
			if (!isValidClient(data.client)) return null
			return { client: data.client }
		})
		.filter(Boolean)

	const enquiryBookingCreatedByKey = new Map()
	enquiryBookings.forEach((booking) => {
		const existing = enquiryBookingCreatedByKey.get(booking.key)
		if (!existing || booking.bookingCreatedMs < existing) {
			enquiryBookingCreatedByKey.set(booking.key, booking.bookingCreatedMs)
		}
	})

	const enquiriesWithMatch = allEnquiries.map((enquiry) => {
		const matchKey = `${enquiry.client}__${enquiry.location}__${enquiry.createdDateKey}`
		const bookingCreatedMs = enquiryBookingCreatedByKey.get(matchKey)
		return {
			...enquiry,
			converted: Number.isFinite(bookingCreatedMs),
			bookingCreatedMs,
		}
	})

	const bookedEnquiries = enquiriesWithMatch.filter((enquiry) => enquiry.converted)
	const allBookingClientsSet = new Set([...enquiryBookings.map((booking) => booking.client), ...directBookings.map((booking) => booking.client)])

	const dedupedNonBookedClients = new Set()
	const dedupedNonBookedEnquiries = enquiriesWithMatch.filter((enquiry) => {
		if (enquiry.converted) return false
		if (allBookingClientsSet.has(enquiry.client)) return false
		if (dedupedNonBookedClients.has(enquiry.client)) return false
		dedupedNonBookedClients.add(enquiry.client)
		return true
	})

	const finalEnquiries = [...dedupedNonBookedEnquiries, ...bookedEnquiries]
	const totalBookings = bookedEnquiries.length + directBookings.length
	const globalConversionRate = finalEnquiries.length > 0 ? totalBookings / finalEnquiries.length : 0

	const finalEnquiryCountByWeekday = createWeekdayArray(0)
	const convertedEnquiryCountByWeekday = createWeekdayArray(0)
	finalEnquiries.forEach((enquiry) => {
		finalEnquiryCountByWeekday[enquiry.weekdayIndex] += 1
		if (enquiry.converted) convertedEnquiryCountByWeekday[enquiry.weekdayIndex] += 1
	})

	const conversionRateByWeekday = createWeekdayArray(0)
	for (let weekdayIndex = 0; weekdayIndex < WEEKDAY_COUNT; weekdayIndex++) {
		conversionRateByWeekday[weekdayIndex] =
			finalEnquiryCountByWeekday[weekdayIndex] > 0 ? convertedEnquiryCountByWeekday[weekdayIndex] / finalEnquiryCountByWeekday[weekdayIndex] : globalConversionRate
	}

	const lagSumByWeekday = createWeekdayArray(0)
	const lagCountByWeekday = createWeekdayArray(0)
	let lagTotal = 0
	let lagCount = 0

	bookedEnquiries.forEach((enquiry) => {
		const lagDays = Math.max((enquiry.bookingCreatedMs - enquiry.createdMs) / DAY_MS, 0)
		lagSumByWeekday[enquiry.weekdayIndex] += lagDays
		lagCountByWeekday[enquiry.weekdayIndex] += 1
		lagTotal += lagDays
		lagCount += 1
	})

	const globalAvgLagDays = lagCount > 0 ? lagTotal / lagCount : 0
	const avgLagDaysByWeekday = createWeekdayArray(0)
	for (let weekdayIndex = 0; weekdayIndex < WEEKDAY_COUNT; weekdayIndex++) {
		avgLagDaysByWeekday[weekdayIndex] = lagCountByWeekday[weekdayIndex] > 0 ? lagSumByWeekday[weekdayIndex] / lagCountByWeekday[weekdayIndex] : globalAvgLagDays
	}

	return {
		conversionRateByWeekday,
		avgLagDaysByWeekday,
		globalConversionRate,
		globalAvgLagDays,
	}
}

const buildBookingMarginModel = (bookingsCreatedSnapshot) => {
	const marginSumByWeekday = createWeekdayArray(0)
	const marginCountByWeekday = createWeekdayArray(0)
	let marginTotal = 0
	let marginCount = 0

	bookingsCreatedSnapshot.docs.forEach((doc) => {
		const data = doc.data()
		const createdSeconds = data.created?.seconds
		const margin = data.margins?.profit
		if (!createdSeconds || !Number.isFinite(margin)) return
		if (data.client && TEST_ACCOUNT_IDS.includes(data.client)) return

		const weekdayIndex = new Date(createdSeconds * 1000).getDay()
		marginSumByWeekday[weekdayIndex] += Number(margin)
		marginCountByWeekday[weekdayIndex] += 1
		marginTotal += Number(margin)
		marginCount += 1
	})

	const globalAvgMarginPerBooking = marginCount > 0 ? marginTotal / marginCount : 0
	const avgMarginByBookingWeekday = createWeekdayArray(0)

	for (let weekdayIndex = 0; weekdayIndex < WEEKDAY_COUNT; weekdayIndex++) {
		avgMarginByBookingWeekday[weekdayIndex] = marginCountByWeekday[weekdayIndex] > 0 ? marginSumByWeekday[weekdayIndex] / marginCountByWeekday[weekdayIndex] : globalAvgMarginPerBooking
	}

	return {
		avgMarginByBookingWeekday,
		globalAvgMarginPerBooking,
	}
}

const buildOpenEnquiryCohort = (allOpenEnquiriesSnapshot) => {
	return allOpenEnquiriesSnapshot.docs
		.filter((doc) => doc.data().archived_from_stats !== true)
		.map((doc) => {
			const data = doc.data()
			const createdSeconds = data.created?.seconds
			if (!createdSeconds || !isValidClient(data.client)) return null

			return {
				createdMs: createdSeconds * 1000,
				weekdayIndex: new Date(createdSeconds * 1000).getDay(),
			}
		})
		.filter(Boolean)
}

const buildNewEnquiryInflowModel = ({ openEnquiriesSnapshot, removedEnquiriesSnapshot, trailingStartMs, trailingEndMs }) => {
	const enquiryCountByWeekday = createWeekdayArray(0)
	let totalEnquiries = 0

	const addEnquiry = (createdSeconds, clientID) => {
		if (!createdSeconds || !isValidClient(clientID)) return
		const weekdayIndex = new Date(createdSeconds * 1000).getDay()
		enquiryCountByWeekday[weekdayIndex] += 1
		totalEnquiries += 1
	}

	openEnquiriesSnapshot.docs
		.filter((doc) => doc.data().archived_from_stats !== true)
		.forEach((doc) => {
			const data = doc.data()
			addEnquiry(data.created?.seconds, data.client)
		})

	removedEnquiriesSnapshot.docs
		.filter((doc) => doc.data().archived_from_stats !== true)
		.forEach((doc) => {
			const data = doc.data().enquiry || {}
			addEnquiry(data.created?.seconds, data.client)
		})

	const weekdayOccurrences = createWeekdayArray(0)
	const startDate = getStartOfDay(new Date(trailingStartMs))
	const endDate = getStartOfDay(new Date(trailingEndMs))
	let currentDate = new Date(startDate)
	let dayCount = 0
	while (currentDate.getTime() <= endDate.getTime()) {
		weekdayOccurrences[currentDate.getDay()] += 1
		dayCount += 1
		currentDate = addDays(currentDate, 1)
	}

	const avgNewEnquiriesPerDayByWeekday = createWeekdayArray(0)
	for (let weekdayIndex = 0; weekdayIndex < WEEKDAY_COUNT; weekdayIndex++) {
		avgNewEnquiriesPerDayByWeekday[weekdayIndex] = weekdayOccurrences[weekdayIndex] > 0 ? enquiryCountByWeekday[weekdayIndex] / weekdayOccurrences[weekdayIndex] : 0
	}

	const globalAvgNewEnquiriesPerDay = dayCount > 0 ? totalEnquiries / dayCount : 0

	return {
		avgNewEnquiriesPerDayByWeekday,
		globalAvgNewEnquiriesPerDay,
	}
}

function MarginsLineChart({ dateRanges, selectedYear, selectedMonth }) {
	const [bookingsByYear, setBookingsByYear] = useState({})
	const [loading, setLoading] = useState(true)
	const [includeSaturdays, setIncludeSaturdays] = useState(true)
	const [includeSundays, setIncludeSundays] = useState(true)
	const [pipelineForecast, setPipelineForecast] = useState({
		loading: true,
		error: null,
		openEnquiries: [],
		openEnquiriesCount: 0,
		avgNewEnquiriesPerDayByWeekday: createWeekdayArray(0),
		globalAvgNewEnquiriesPerDay: 0,
		conversionRateByWeekday: createWeekdayArray(0),
		avgLagDaysByWeekday: createWeekdayArray(0),
		avgMarginByBookingWeekday: createWeekdayArray(0),
		globalConversionRate: 0,
		globalAvgLagDays: 0,
		globalAvgMarginPerBooking: 0,
	})

	const currencyFormatter = useMemo(
		() =>
			new Intl.NumberFormat("en-GB", {
				style: "currency",
				currency: "GBP",
				maximumFractionDigits: 0,
			}),
		[],
	)

	// Fetch data for all date ranges
	useEffect(() => {
		const fetchAllData = async () => {
			setLoading(true)
			const dataByYear = {}

			for (const range of dateRanges) {
				const bookings = await db
					.collection("bookings")
					.where("created", ">", range.start)
					.where("created", "<", range.end)
					.get()
					.then((bookingDocs) => {
						const bookingsList = []
						let totalMargin = 0
						bookingDocs.forEach((bookingDoc) => {
							const data = bookingDoc.data()
							bookingsList.push({ id: bookingDoc.id, ...data })
							totalMargin += Math.round(data.margins?.profit) || 0
						})
						return { bookings: bookingsList, total: totalMargin }
					})

				dataByYear[range.yearOffset] = {
					...bookings,
					year: range.year,
				}
			}

			setBookingsByYear(dataByYear)
			setLoading(false)
		}

		fetchAllData()
	}, [dateRanges])

	useEffect(() => {
		let isCancelled = false

		const fetchPipelineForecast = async () => {
			setPipelineForecast((prev) => ({
				...prev,
				loading: true,
				error: null,
			}))

			try {
				const nowMs = Date.now()
				const trailingStartMs = nowMs - 90 * DAY_MS
				const trailingStartTs = firebase.firestore.Timestamp.fromMillis(trailingStartMs)
				const trailingEndTs = firebase.firestore.Timestamp.fromMillis(nowMs)

				const [allOpenEnquiriesSnapshot, openEnquiriesTrailingSnapshot, removedEnquiriesTrailingSnapshot, enquiryBookingsTrailingSnapshot, bookingsCreatedTrailingSnapshot] = await Promise.all([
					db.collection("enquiries").get(),
					db.collection("enquiries").where("created", ">=", trailingStartTs).where("created", "<=", trailingEndTs).get(),
					db.collection("enquiries_removed").where("enquiry.created", ">=", trailingStartTs).where("enquiry.created", "<=", trailingEndTs).get(),
					db.collection("bookings").where("enquiry.created", ">=", trailingStartTs).where("enquiry.created", "<=", trailingEndTs).get(),
					db.collection("bookings").where("created", ">=", trailingStartTs).where("created", "<=", trailingEndTs).get(),
				])

				const conversionModel = buildConversionAndLagModel({
					openEnquiriesSnapshot: openEnquiriesTrailingSnapshot,
					removedEnquiriesSnapshot: removedEnquiriesTrailingSnapshot,
					enquiryBookingsSnapshot: enquiryBookingsTrailingSnapshot,
					bookingsCreatedSnapshot: bookingsCreatedTrailingSnapshot,
				})

				const marginModel = buildBookingMarginModel(bookingsCreatedTrailingSnapshot)
				const newEnquiryInflowModel = buildNewEnquiryInflowModel({
					openEnquiriesSnapshot: openEnquiriesTrailingSnapshot,
					removedEnquiriesSnapshot: removedEnquiriesTrailingSnapshot,
					trailingStartMs,
					trailingEndMs: nowMs,
				})
				const openEnquiries = buildOpenEnquiryCohort(allOpenEnquiriesSnapshot)

				if (isCancelled) return

				setPipelineForecast({
					loading: false,
					error: null,
					openEnquiries,
					openEnquiriesCount: openEnquiries.length,
					avgNewEnquiriesPerDayByWeekday: newEnquiryInflowModel.avgNewEnquiriesPerDayByWeekday,
					globalAvgNewEnquiriesPerDay: newEnquiryInflowModel.globalAvgNewEnquiriesPerDay,
					conversionRateByWeekday: conversionModel.conversionRateByWeekday,
					avgLagDaysByWeekday: conversionModel.avgLagDaysByWeekday,
					avgMarginByBookingWeekday: marginModel.avgMarginByBookingWeekday,
					globalConversionRate: conversionModel.globalConversionRate,
					globalAvgLagDays: conversionModel.globalAvgLagDays,
					globalAvgMarginPerBooking: marginModel.globalAvgMarginPerBooking,
				})
			} catch (error) {
				if (isCancelled) return
				setPipelineForecast({
					loading: false,
					error: error?.message || "Unable to load pipeline forecast",
					openEnquiries: [],
					openEnquiriesCount: 0,
					avgNewEnquiriesPerDayByWeekday: createWeekdayArray(0),
					globalAvgNewEnquiriesPerDay: 0,
					conversionRateByWeekday: createWeekdayArray(0),
					avgLagDaysByWeekday: createWeekdayArray(0),
					avgMarginByBookingWeekday: createWeekdayArray(0),
					globalConversionRate: 0,
					globalAvgLagDays: 0,
					globalAvgMarginPerBooking: 0,
				})
			}
		}

		fetchPipelineForecast()

		return () => {
			isCancelled = true
		}
	}, [selectedYear, selectedMonth])

	const periodDates = useMemo(() => {
		const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate()
		return Array.from({ length: daysInMonth }, (_, index) => new Date(selectedYear, selectedMonth, index + 1))
	}, [selectedYear, selectedMonth])

	const allDates = useMemo(() => {
		return periodDates.map((date) => formatDayMonthKey(date))
	}, [periodDates])

	const isCurrentMonth = useMemo(() => {
		const now = new Date()
		return selectedYear === now.getFullYear() && selectedMonth === now.getMonth()
	}, [selectedYear, selectedMonth])

	const todayDate = useMemo(() => {
		const now = new Date()
		return new Date(now.getFullYear(), now.getMonth(), now.getDate())
	}, [])

	const asOfDate = useMemo(() => {
		const now = new Date()
		const currentMonthIndex = now.getFullYear() * 12 + now.getMonth()
		const selectedMonthIndex = selectedYear * 12 + selectedMonth

		if (selectedMonthIndex === currentMonthIndex) {
			return todayDate
		}

		if (selectedMonthIndex < currentMonthIndex) {
			return new Date(selectedYear, selectedMonth + 1, 0)
		}

		return new Date(selectedYear, selectedMonth, 0)
	}, [selectedYear, selectedMonth, todayDate])

	// Process margin data with memoization
	const processMargins = useMemo(() => {
		return (data) => {
			const counts = {}

			data.forEach((booking) => {
				const date = new Date(booking.created.seconds * 1000)
				const normalizedDate = formatDayMonthKey(date)

				const profit = booking.margins?.profit || 0
				const roundedProfit = Math.round(profit)

				counts[normalizedDate] = (counts[normalizedDate] || 0) + roundedProfit
			})

			const sortedDates = Object.keys(counts).sort((a, b) => {
				const [dayA, monthA] = a.split("/").map(Number)
				const [dayB, monthB] = b.split("/").map(Number)
				return monthA === monthB ? dayA - dayB : monthA - monthB
			})

			const cumulativeMargins = {}
			const dailyMargins = {}
			let runningTotal = 0
			sortedDates.forEach((date) => {
				runningTotal += counts[date]
				cumulativeMargins[date] = runningTotal
				dailyMargins[date] = counts[date]
			})

			return { cumulativeMargins, dailyMargins }
		}
	}, [])

	const processedData = useMemo(() => {
		const processed = {}
		Object.keys(bookingsByYear).forEach((yearOffset) => {
			processed[yearOffset] = processMargins(bookingsByYear[yearOffset].bookings)
		})
		return processed
	}, [bookingsByYear, processMargins])

	const currentYearCumulativeSeries = useMemo(() => {
		const currentYearCumulative = processedData[0]?.cumulativeMargins || {}
		return allDates.map((dateKey) => getCumulativeValueForDate(dateKey, allDates, currentYearCumulative))
	}, [processedData, allDates])

	const runRateSummary = useMemo(() => {
		const isIncludedDay = (date) => {
			const day = date.getDay()
			if (!includeSaturdays && day === 6) return false
			if (!includeSundays && day === 0) return false
			return true
		}

		let actualAtAsOf = 0
		periodDates.forEach((date, index) => {
			if (date <= asOfDate) {
				actualAtAsOf = currentYearCumulativeSeries[index] || 0
			}
		})

		const remainingSelectedDays = periodDates.filter((date) => date > asOfDate && isIncludedDay(date)).length
		const remainingTarget = Math.max(TARGET_MARGIN - actualAtAsOf, 0)
		const requiredPerIncludedDay = remainingSelectedDays > 0 ? remainingTarget / remainingSelectedDays : 0

		let runningRunRate = actualAtAsOf
		const runRateSeries = periodDates.map((date, index) => {
			if (date <= asOfDate) {
				return currentYearCumulativeSeries[index] || 0
			}

			if (isIncludedDay(date)) {
				runningRunRate += requiredPerIncludedDay
			}

			return Math.round(runningRunRate)
		})

		return {
			runRateSeries,
			requiredPerIncludedDay,
			remainingSelectedDays,
			remainingTarget,
			actualAtAsOf,
		}
	}, [periodDates, asOfDate, currentYearCumulativeSeries, includeSaturdays, includeSundays])

	const pipelineForecastSummaries = useMemo(() => {
		const baseSeries = periodDates.map((date, index) => {
			return currentYearCumulativeSeries[index] || 0
		})

		const emptySummary = {
			forecastSeries: baseSeries,
			monthEndForecast: baseSeries[baseSeries.length - 1] || 0,
			expectedAdditionalMarginInMonth: 0,
			expectedAdditionalMarginTotal: 0,
			expectedAdditionalFromOpenEnquiriesInMonth: 0,
			expectedAdditionalFromNewEnquiriesInMonth: 0,
			overdueExcludedCount: 0,
		}

		if (pipelineForecast.loading || pipelineForecast.error) {
			return {
				live: emptySummary,
				startOfMonth: emptySummary,
			}
		}

		const periodStartDate = periodDates[0]
		const periodEndDate = periodDates[periodDates.length - 1]
		const startOfMonthAsOfDate = new Date(selectedYear, selectedMonth, 0)

		const maxLagDaysForCarryIn = Math.max(
			1,
			Math.ceil(Math.max(pipelineForecast.globalAvgLagDays || 0, ...(pipelineForecast.avgLagDaysByWeekday || []).map((value) => (Number.isFinite(value) ? value : 0)))),
		)

		const addExpectedMarginForEnquiryDate = ({ enquiryDate, dailyForecastByDate }) => {
			const enquiryWeekday = enquiryDate.getDay()
			const expectedNewEnquiriesForDay = getMetricForWeekday(pipelineForecast.avgNewEnquiriesPerDayByWeekday, enquiryWeekday, pipelineForecast.globalAvgNewEnquiriesPerDay)

			if (expectedNewEnquiriesForDay <= 0) {
				return { expectedMargin: 0, inMonthMargin: 0 }
			}

			const conversionRate = getMetricForWeekday(pipelineForecast.conversionRateByWeekday, enquiryWeekday, pipelineForecast.globalConversionRate)
			const avgLagDays = getMetricForWeekday(pipelineForecast.avgLagDaysByWeekday, enquiryWeekday, pipelineForecast.globalAvgLagDays)
			const projectedBookingDate = addDays(enquiryDate, Math.ceil(avgLagDays))
			const bookingWeekday = projectedBookingDate.getDay()
			const avgMarginForBookingDay = getMetricForWeekday(pipelineForecast.avgMarginByBookingWeekday, bookingWeekday, pipelineForecast.globalAvgMarginPerBooking)
			const expectedMargin = expectedNewEnquiriesForDay * conversionRate * avgMarginForBookingDay

			if (projectedBookingDate.getTime() <= periodEndDate.getTime()) {
				const projectedDateKey = toLocalDateKey(projectedBookingDate)
				dailyForecastByDate[projectedDateKey] = (dailyForecastByDate[projectedDateKey] || 0) + expectedMargin

				if (projectedBookingDate.getTime() >= periodStartDate.getTime()) {
					return { expectedMargin, inMonthMargin: expectedMargin }
				}
			}

			return { expectedMargin, inMonthMargin: 0 }
		}

		const buildSummaryForAsOf = ({ modelAsOfDate, includeActualAcrossChart, includePriorMonthCarryIn = false }) => {
			const asOfStart = getStartOfDay(modelAsOfDate)
			const dailyForecastByDate = {}
			let expectedAdditionalMarginTotal = 0
			let expectedAdditionalMarginInMonth = 0
			let expectedAdditionalFromOpenEnquiriesInMonth = 0
			let expectedAdditionalFromNewEnquiriesInMonth = 0
			let overdueExcludedCount = 0

			pipelineForecast.openEnquiries.forEach((enquiry) => {
				if (enquiry.createdMs > asOfStart.getTime()) {
					return
				}

				const conversionRate = getMetricForWeekday(pipelineForecast.conversionRateByWeekday, enquiry.weekdayIndex, pipelineForecast.globalConversionRate)
				const avgLagDays = getMetricForWeekday(pipelineForecast.avgLagDaysByWeekday, enquiry.weekdayIndex, pipelineForecast.globalAvgLagDays)
				const enquiryStartDate = getStartOfDay(new Date(enquiry.createdMs))
				const ageDays = Math.max((asOfStart.getTime() - enquiryStartDate.getTime()) / DAY_MS, 0)

				if (ageDays > avgLagDays) {
					overdueExcludedCount += 1
					return
				}

				let projectedBookingDate = addDays(enquiryStartDate, Math.ceil(avgLagDays))
				if (projectedBookingDate.getTime() < periodStartDate.getTime()) {
					projectedBookingDate = new Date(periodStartDate)
				}

				const bookingWeekday = projectedBookingDate.getDay()
				const avgMarginForBookingDay = getMetricForWeekday(pipelineForecast.avgMarginByBookingWeekday, bookingWeekday, pipelineForecast.globalAvgMarginPerBooking)
				const expectedMarginForEnquiry = conversionRate * avgMarginForBookingDay

				expectedAdditionalMarginTotal += expectedMarginForEnquiry
				if (projectedBookingDate.getTime() > periodEndDate.getTime()) {
					return
				}

				const projectedDateKey = toLocalDateKey(projectedBookingDate)
				dailyForecastByDate[projectedDateKey] = (dailyForecastByDate[projectedDateKey] || 0) + expectedMarginForEnquiry
				expectedAdditionalMarginInMonth += expectedMarginForEnquiry
				expectedAdditionalFromOpenEnquiriesInMonth += expectedMarginForEnquiry
			})

			if (includePriorMonthCarryIn) {
				const carryStartDate = addDays(periodStartDate, -maxLagDaysForCarryIn)
				const carryEndDate = addDays(periodStartDate, -1)
				let carryEnquiryDate = new Date(carryStartDate)

				while (carryEnquiryDate.getTime() <= carryEndDate.getTime()) {
					const carryResult = addExpectedMarginForEnquiryDate({
						enquiryDate: carryEnquiryDate,
						dailyForecastByDate,
					})

					expectedAdditionalMarginTotal += carryResult.expectedMargin
					expectedAdditionalMarginInMonth += carryResult.inMonthMargin
					expectedAdditionalFromNewEnquiriesInMonth += carryResult.inMonthMargin
					carryEnquiryDate = addDays(carryEnquiryDate, 1)
				}
			}

			const firstNewEnquiryDate = addDays(asOfStart, 1)
			let forecastEnquiryDate = new Date(firstNewEnquiryDate)
			while (forecastEnquiryDate.getTime() <= periodEndDate.getTime()) {
				const forecastResult = addExpectedMarginForEnquiryDate({
					enquiryDate: forecastEnquiryDate,
					dailyForecastByDate,
				})

				expectedAdditionalMarginTotal += forecastResult.expectedMargin
				expectedAdditionalMarginInMonth += forecastResult.inMonthMargin
				expectedAdditionalFromNewEnquiriesInMonth += forecastResult.inMonthMargin

				forecastEnquiryDate = addDays(forecastEnquiryDate, 1)
			}

			const rolledForecastByDate = rollForecastIntoIncludedDays({
				dailyForecastByDate,
				periodDates,
				includeSaturdays,
				includeSundays,
			})

			let actualAtAsOf = 0
			periodDates.forEach((date, index) => {
				if (date <= asOfStart) {
					actualAtAsOf = currentYearCumulativeSeries[index] || 0
				}
			})

			let runningProjected = 0
			const forecastSeries = periodDates.map((date, index) => {
				runningProjected += rolledForecastByDate[toLocalDateKey(date)] || 0

				if (includeActualAcrossChart) {
					return Math.round((currentYearCumulativeSeries[index] || 0) + runningProjected)
				}

				if (date <= asOfStart) {
					return currentYearCumulativeSeries[index] || 0
				}

				return Math.round(actualAtAsOf + runningProjected)
			})

			return {
				forecastSeries,
				monthEndForecast: forecastSeries[forecastSeries.length - 1] || 0,
				expectedAdditionalMarginInMonth,
				expectedAdditionalMarginTotal,
				expectedAdditionalFromOpenEnquiriesInMonth,
				expectedAdditionalFromNewEnquiriesInMonth,
				overdueExcludedCount,
			}
		}

		return {
			live: buildSummaryForAsOf({ modelAsOfDate: asOfDate, includeActualAcrossChart: true }),
			startOfMonth: buildSummaryForAsOf({
				modelAsOfDate: startOfMonthAsOfDate,
				includeActualAcrossChart: false,
				includePriorMonthCarryIn: true,
			}),
		}
	}, [pipelineForecast, periodDates, asOfDate, currentYearCumulativeSeries, includeSaturdays, includeSundays, selectedYear, selectedMonth])

	const chartData = useMemo(() => {
		const datasets = []

		Object.keys(bookingsByYear)
			.sort((a, b) => parseInt(a) - parseInt(b))
			.forEach((yearOffset) => {
				const offset = parseInt(yearOffset)
				const yearData = processedData[offset]?.cumulativeMargins || {}
				const color = getColorForYear(offset)
				const year = bookingsByYear[offset].year

				const data = allDates.map((dateKey, index) => {
					const periodDate = periodDates[index]
					if (offset === 0 && isCurrentMonth && periodDate > todayDate) {
						return null
					}
					return getCumulativeValueForDate(dateKey, allDates, yearData)
				})

				datasets.push({
					label: offset === 0 ? `${year} (Current)` : `${year}`,
					data,
					borderColor: color.border,
					backgroundColor: color.bg,
					borderWidth: offset === 0 ? 3 : 2,
					pointRadius: offset === 0 ? 3 : 1,
					pointHoverRadius: offset === 0 ? 5 : 3,
					borderDash: offset === 0 ? [] : [5, 5],
					fill: false,
					tension: 0.1,
					datasetType: "year",
					yearOffset: offset,
					hidden: offset === 1,
				})
			})

		const linearTargetDataset = allDates.map((_, index) => Math.round((TARGET_MARGIN / allDates.length) * (index + 1)))
		datasets.push({
			label: "Target Margin",
			data: linearTargetDataset,
			borderColor: "rgba(147, 51, 234, 0.6)",
			backgroundColor: "rgba(147, 51, 234, 0.6)",
			pointRadius: 0,
			borderDash: [10, 5],
			borderWidth: 2,
			fill: false,
			datasetType: "target",
		})

		datasets.push({
			label: "Run-rate",
			data: runRateSummary.runRateSeries,
			borderColor: "rgba(22, 163, 74, 0.85)",
			backgroundColor: "rgba(22, 163, 74, 0.85)",
			pointRadius: 0,
			pointHoverRadius: 4,
			borderDash: [6, 4],
			borderWidth: 2,
			fill: false,
			tension: 0.1,
			datasetType: "run_rate",
			requiredPerIncludedDay: runRateSummary.requiredPerIncludedDay,
		})

		if (!pipelineForecast.loading && !pipelineForecast.error) {
			datasets.push({
				label: "Forecast",
				data: pipelineForecastSummaries.live.forecastSeries,
				borderColor: "rgba(37, 99, 235, 0.85)",
				backgroundColor: "rgba(37, 99, 235, 0.85)",
				pointRadius: 0,
				pointHoverRadius: 4,
				borderDash: [3, 3],
				borderWidth: 2,
				fill: false,
				tension: 0.1,
				datasetType: "forecast_pipeline",
				openEnquiriesCount: pipelineForecast.openEnquiriesCount,
				overdueExcludedCount: pipelineForecastSummaries.live.overdueExcludedCount,
				trailingAvgNewEnquiriesPerDay: pipelineForecast.globalAvgNewEnquiriesPerDay,
				trailingConversionRate: pipelineForecast.globalConversionRate,
				trailingAvgConversionDays: pipelineForecast.globalAvgLagDays,
				trailingAvgMarginPerBooking: pipelineForecast.globalAvgMarginPerBooking,
			})

			datasets.push({
				label: "Forecast (Start month)",
				data: pipelineForecastSummaries.startOfMonth.forecastSeries,
				borderColor: "rgba(245, 158, 11, 0.85)",
				backgroundColor: "rgba(245, 158, 11, 0.85)",
				pointRadius: 0,
				pointHoverRadius: 4,
				borderDash: [2, 6],
				borderWidth: 2,
				fill: false,
				tension: 0.1,
				datasetType: "forecast_pipeline_start",
				openEnquiriesCount: pipelineForecast.openEnquiriesCount,
				overdueExcludedCount: pipelineForecastSummaries.startOfMonth.overdueExcludedCount,
				trailingAvgNewEnquiriesPerDay: pipelineForecast.globalAvgNewEnquiriesPerDay,
				trailingConversionRate: pipelineForecast.globalConversionRate,
				trailingAvgConversionDays: pipelineForecast.globalAvgLagDays,
				trailingAvgMarginPerBooking: pipelineForecast.globalAvgMarginPerBooking,
				hidden: true,
			})
		}

		return { labels: allDates, datasets }
	}, [bookingsByYear, processedData, allDates, periodDates, todayDate, isCurrentMonth, runRateSummary, pipelineForecast, pipelineForecastSummaries])

	const chartOptions = useMemo(() => {
		const currentYearDataset = chartData.datasets.find((dataset) => dataset.datasetType === "year" && dataset.yearOffset === 0)

		return {
			responsive: true,
			maintainAspectRatio: false,
			animations: {
				y: {
					easing: "easeOutQuart",
					duration: 1500,
				},
				x: {
					duration: 0,
				},
			},
			plugins: {
				legend: {
					position: "top",
					labels: {
						usePointStyle: true,
						padding: 15,
						font: {
							size: 12,
							weight: "500",
						},
					},
				},
				tooltip: {
					enabled: true,
					mode: "index",
					intersect: false,
					backgroundColor: "rgba(255, 255, 255, 0.95)",
					titleColor: "#111827",
					bodyColor: "#374151",
					borderColor: "#e5e7eb",
					borderWidth: 1,
					padding: 12,
					boxPadding: 6,
					usePointStyle: true,
					callbacks: {
						label: function (context) {
							const value = context.raw
							const date = context.label
							const datasetType = context.dataset.datasetType

							if (value === null || value === undefined) {
								return null
							}

							const formattedValue = currencyFormatter.format(value)

							if (datasetType === "target") {
								return `${context.dataset.label}: ${formattedValue}`
							}

							if (datasetType === "run_rate") {
								return [`${context.dataset.label}: ${formattedValue}`, `Required / selected day: ${currencyFormatter.format(context.dataset.requiredPerIncludedDay || 0)}`]
							}

							if (datasetType === "forecast_pipeline" || datasetType === "forecast_pipeline_start") {
								return [
									`${context.dataset.label}: ${formattedValue}`,
									`Open enquiries: ${context.dataset.openEnquiriesCount || 0}`,
									`Overdue excluded: ${context.dataset.overdueExcludedCount || 0}`,
									`New enquiries/day (90d): ${(context.dataset.trailingAvgNewEnquiriesPerDay || 0).toFixed(2)}`,
									`Conv. rate (90d): ${((context.dataset.trailingConversionRate || 0) * 100).toFixed(2)}%`,
									`Avg conversion days (90d): ${(context.dataset.trailingAvgConversionDays || 0).toFixed(1)}`,
									`Avg margin/booking (90d): ${currencyFormatter.format(context.dataset.trailingAvgMarginPerBooking || 0)}`,
								]
							}

							const yearOffset = context.dataset.yearOffset
							const dailyData = processedData[yearOffset]?.dailyMargins || {}
							const dailyTotal = dailyData[date] || 0
							const formattedDaily = currencyFormatter.format(dailyTotal)

							if (yearOffset !== 0 && currentYearDataset) {
								const currentYearValue = currentYearDataset.data[context.dataIndex]
								if (currentYearValue === null || currentYearValue === undefined) {
									return [`${context.dataset.label}: ${formattedValue}`, `Daily Total: ${formattedDaily}`]
								}

								const diff = currentYearValue - value
								const percentageDiff = value ? ((diff / value) * 100).toFixed(1) : "N/A"
								return [`${context.dataset.label}: ${formattedValue} (${diff >= 0 ? "+" : ""}${percentageDiff}% vs current)`, `Daily Total: ${formattedDaily}`]
							}

							return [`${context.dataset.label}: ${formattedValue}`, `Daily Total: ${formattedDaily}`]
						},
					},
				},
			},
			interaction: {
				mode: "index",
				intersect: false,
			},
			scales: {
				x: {
					title: {
						display: true,
						text: "Date (DD/MM)",
						font: { size: 12, weight: "600" },
						color: "#374151",
					},
					grid: {
						color: "rgba(229, 231, 235, 0.5)",
						drawBorder: false,
					},
					ticks: {
						maxRotation: 45,
						minRotation: 45,
						font: { size: 10 },
						color: "#6b7280",
					},
				},
				y: {
					title: {
						display: true,
						text: "Cumulative Margin (£)",
						font: { size: 12, weight: "600" },
						color: "#374151",
					},
					grid: {
						color: "rgba(229, 231, 235, 0.5)",
						drawBorder: false,
					},
					ticks: {
						font: { size: 10 },
						color: "#6b7280",
						callback: function (value) {
							return (
								"£" +
								new Intl.NumberFormat("en-GB", {
									notation: "compact",
									compactDisplay: "short",
								}).format(value)
							)
						},
					},
				},
			},
		}
	}, [chartData, processedData, currencyFormatter])

	if (loading) {
		return (
			<div>
				<h2>Margin Trends</h2>
				<p>Loading data...</p>
			</div>
		)
	}

	return (
		<div>
			<h2>Margin Trends</h2>
			<div className="margin-forecast-controls">
				<Checkbox
					label="Include Saturdays"
					checked={includeSaturdays}
					onClick={() => setIncludeSaturdays(!includeSaturdays)}
				/>
				<Checkbox
					label="Include Sundays"
					checked={includeSundays}
					onClick={() => setIncludeSundays(!includeSundays)}
				/>
			</div>

			<div className="margin-forecast-meta">
				<div className="margin-forecast-panel run-rate-meta">
					<p className="margin-forecast-panel-title">Run-rate</p>
					<div className="margin-forecast-metrics">
						<span>
							<span className="margin-forecast-label">Required / selected day</span>
							<strong>{currencyFormatter.format(runRateSummary.requiredPerIncludedDay)}</strong>
						</span>
						<span>
							<span className="margin-forecast-label">Remaining selected days</span>
							<strong>{runRateSummary.remainingSelectedDays}</strong>
						</span>
					</div>
					{runRateSummary.remainingSelectedDays === 0 && runRateSummary.remainingTarget > 0 && <span className="margin-forecast-warning">No selected days remaining to reach target.</span>}
				</div>

				<div className="margin-forecast-panel forecast-meta">
					<p className="margin-forecast-panel-title">Pipeline Forecast</p>
					{pipelineForecast.loading && <span>Loading pipeline forecast...</span>}
					{!pipelineForecast.loading && pipelineForecast.error && <span className="margin-forecast-error">Pipeline forecast unavailable.</span>}
					{!pipelineForecast.loading && !pipelineForecast.error && (
						<>
							<div className="margin-forecast-metrics">
								<span>
									<span className="margin-forecast-label">Month-end (live)</span>
									<strong>{currencyFormatter.format(pipelineForecastSummaries.live.monthEndForecast)}</strong>
								</span>
								<span>
									<span className="margin-forecast-label">Month-end (start month)</span>
									<strong>{currencyFormatter.format(pipelineForecastSummaries.startOfMonth.monthEndForecast)}</strong>
								</span>
								<span>
									<span className="margin-forecast-label">Open enquiries</span>
									<strong>{pipelineForecast.openEnquiriesCount}</strong>
								</span>
								<span>
									<span className="margin-forecast-label">Overdue excluded</span>
									<strong>{pipelineForecastSummaries.live.overdueExcludedCount}</strong>
								</span>
							</div>

							<div className="margin-forecast-model">
								<span>New enquiries/day (90d): {pipelineForecast.globalAvgNewEnquiriesPerDay.toFixed(2)}</span>
								<span>Conversion (90d): {(pipelineForecast.globalConversionRate * 100).toFixed(2)}%</span>
								<span>Avg conversion days (90d): {pipelineForecast.globalAvgLagDays.toFixed(1)}</span>
								<span>Avg margin/booking (90d): {currencyFormatter.format(pipelineForecast.globalAvgMarginPerBooking)}</span>
							</div>

							{runRateSummary.remainingSelectedDays === 0 && (pipelineForecast.openEnquiriesCount > 0 || pipelineForecast.globalAvgNewEnquiriesPerDay > 0) && (
								<span className="margin-forecast-warning">No selected days remaining to distribute forecast.</span>
							)}
						</>
					)}
				</div>
			</div>

			<div className="margin-year-totals">
				{Object.keys(bookingsByYear)
					.sort((a, b) => parseInt(a) - parseInt(b))
					.map((yearOffset) => {
						const offset = parseInt(yearOffset)
						const data = bookingsByYear[offset]
						return (
							<span
								className="margin-year-total"
								key={offset}>
								{offset === 0 ? "Current" : `${data.year}`}: {currencyFormatter.format(data.total)}
							</span>
						)
					})}
			</div>
			<div style={{ height: "600px", width: "100%" }}>
				<Line
					data={chartData}
					options={chartOptions}
				/>
			</div>
		</div>
	)
}

export default memo(MarginsLineChart)
